/**
 * Packages — shared between the Package builder and package pickers
 * (Create QCS organisation / Manage Limits).
 *
 * Catalog IO goes through `src/tabs/home/api/packages.js`. This module maps
 * API DTOs ↔ the UI shape the builder already uses, and keeps an in-memory
 * cache so sync helpers (`loadPackages`, org row pills) still work.
 *
 * UI status labels stay Draft / Live; the API uses DRAFT / ACTIVE (INACTIVE
 * is treated as Draft in the builder so we do not add a third chip).
 * `usageLimit.unlimited` and `carePlanEnabled` are first-class API fields.
 */

import {
  SUPERVISION_MODE,
  SUPERVISION_MODE_LABEL,
  SUPERVISION_MODE_OPTIONS,
} from '../../../constants/supervisionMode'
import { listAllPackages } from '../api/packages'

export const PACKAGES_STORAGE_KEY = 'carebrainPackages'
const UI_FLAGS_KEY = 'carebrainPackageUiFlags'

// Lucide glyph path sets, drawn inline (the repo ships no lucide package). Data
// only — the components own the <svg> wrapper.
export const ICON = {
  // `box`
  box: [
    'M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z',
    'm3.3 7 8.7 5 8.7-5',
    'M12 22V12',
  ],
  // `tag`
  tag: [
    'M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z',
    'M7.5 7.5h.01',
  ],
  // `users`
  users: [
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2',
    'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
    'M22 21v-2a4 4 0 0 0-3-3.87',
    'M16 3.13a4 4 0 0 1 0 7.75',
  ],
  // `clipboard-check`
  clipboardCheck: [
    'M9 4h6a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z',
    'M16 5h1a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h1',
    'M9 14l2 2 4-4',
  ],
  search: ['M11 3a8 8 0 1 0 0 16 8 8 0 0 0 0-16Z', 'm21 21-4.3-4.3'],
  arrowLeft: ['m12 19-7-7 7-7', 'M19 12H5'],
  plus: ['M12 5v14', 'M5 12h14'],
  minus: ['M5 12h14'],
  check: ['M20 6 9 17l-5-5'],
  trash: [
    'M3 6h18',
    'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6',
    'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2',
    'M10 11v6',
    'M14 11v6',
  ],
  // lucide `pencil`
  pencil: [
    'M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z',
    'm15 5 4 4',
  ],
}

// What each supervision mode lets the organisation do — shown wherever
// supervision is described so operators know what they are selling.
export const SUPERVISION_MODE_DESC = {
  [SUPERVISION_MODE.DEFAULT]:
    'Add your own users, but the supervision questions are fixed — they cannot be added, edited, or removed.',
  [SUPERVISION_MODE.DYNAMIC]:
    'Add your own users and edit or create supervision questions to suit the organisation',
}

// Tier level is derived from the number of features switched on.
export const TIER_LEVELS = ['Basic', 'Gold', 'Platinum']

export const TIER_LEVEL_META = {
  Basic: { chipBg: 'var(--careco-primary-light)', chipColor: 'var(--careco-primary)' },
  Gold: { chipBg: '#FCF3DA', chipColor: '#B58A1B' },
  Platinum: { chipBg: '#EFECFB', chipColor: '#6C5CE7' },
}

let idCounter = 0
const newTempId = () => `tmp_${Date.now().toString(36)}_${(idCounter++).toString(36)}`

/** True for packages that exist only in the builder until the first POST. */
export const isTempPackageId = id => String(id || '').startsWith('tmp_')

// UI labels stay Draft / Live; API uses DRAFT / ACTIVE (INACTIVE → Draft).
export const PACKAGE_STATUS = { DRAFT: 'draft', LIVE: 'live' }

export const uiStatusFromApi = status =>
  (String(status || '').toUpperCase() === 'ACTIVE' ? PACKAGE_STATUS.LIVE : PACKAGE_STATUS.DRAFT)

export const apiStatusFromUi = status =>
  (status === PACKAGE_STATUS.LIVE ? 'ACTIVE' : 'DRAFT')

const DEFAULT_USAGE_RANGE = { min: 1, max: 10000, unit: 'USAGE' }
let usageLimitRange = { ...DEFAULT_USAGE_RANGE }

export const getCachedUsageLimitRange = () => usageLimitRange

export const setCachedUsageLimitRange = range => {
  if (!range) return usageLimitRange
  const min = Number(range.min)
  const max = Number(range.max)
  usageLimitRange = {
    min: Number.isFinite(min) && min > 0 ? min : DEFAULT_USAGE_RANGE.min,
    max: Number.isFinite(max) && max >= usageLimitRange.min ? max : DEFAULT_USAGE_RANGE.max,
    unit: range.unit || DEFAULT_USAGE_RANGE.unit,
  }
  return usageLimitRange
}

const readUiFlags = () => {
  try {
    const raw = window.localStorage.getItem(UI_FLAGS_KEY)
    return raw ? JSON.parse(raw) || {} : {}
  } catch {
    return {}
  }
}

const writeUiFlags = map => {
  try {
    window.localStorage.setItem(UI_FLAGS_KEY, JSON.stringify(map))
  } catch {
    // ignore quota / private mode
  }
}

export const setPackageUiFlags = (packageId, patch) => {
  if (packageId == null || isTempPackageId(packageId)) return
  const key = String(packageId)
  const map = readUiFlags()
  const next = { ...(map[key] || {}), ...patch }
  if (!next.auditUnlimited) {
    delete next.auditUnlimited
  }
  if (Object.keys(next).length === 0) delete map[key]
  else map[key] = next
  writeUiFlags(map)
}

export const clearPackageUiFlags = packageId => {
  if (packageId == null) return
  const map = readUiFlags()
  delete map[String(packageId)]
  writeUiFlags(map)
}

export const makeEmptyPackage = () => ({
  id: newTempId(),
  name: '',
  supervision: false,
  supervisionModes: [SUPERVISION_MODE.DEFAULT],
  carePlanAudit: false,
  auditLimit: '20',
  auditUnlimited: false,
  status: PACKAGE_STATUS.DRAFT,
})

/**
 * Map PackageResponse → UI package shape (Draft/Live, feature toggles, etc.).
 */
export const fromPackageResponse = dto => {
  if (!dto) return makeEmptyPackage()

  const id = String(dto.id)
  const flags = readUiFlags()[id] || {}
  const usage = dto.usageLimit || {}
  const limitOn = Boolean(usage.enabled)
  const preservedLimit =
    usage.limit != null && Number(usage.limit) > 0 ? String(usage.limit) : '20'

  // Prefer the API unlimited flag; fall back to the old local flag only when
  // the field is absent (pre-unlimited payloads).
  const auditUnlimited =
    usage.unlimited === true ||
    (usage.unlimited == null && Boolean(flags.auditUnlimited) && !limitOn)
  const carePlanAudit =
    dto.carePlanEnabled != null ? Boolean(dto.carePlanEnabled) : limitOn || auditUnlimited

  const defaultEnabled = Boolean(dto.supervision?.defaultEnabled)
  const dynamicEnabled = Boolean(dto.supervision?.dynamicEnabled)
  const supervisionModes = SUPERVISION_MODE_OPTIONS.filter(mode => {
    if (mode === SUPERVISION_MODE.DEFAULT) return defaultEnabled || dynamicEnabled
    if (mode === SUPERVISION_MODE.DYNAMIC) return dynamicEnabled
    return false
  })
  // Dynamic implies Default in the UI — keep the stored order stable.
  const modes =
    supervisionModes.length > 0
      ? SUPERVISION_MODE_OPTIONS.filter(m => supervisionModes.includes(m))
      : []

  return {
    id,
    name: dto.name || '',
    supervision: defaultEnabled || dynamicEnabled,
    supervisionModes: modes.length ? modes : [],
    carePlanAudit,
    auditLimit: preservedLimit,
    auditUnlimited,
    status: uiStatusFromApi(dto.status),
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    createdBy: dto.createdBy,
    updatedBy: dto.updatedBy,
  }
}

/**
 * Map UI package → CreatePackageRequest / UpdatePackageRequest body.
 * Status is never sent (create is always DRAFT; use PATCH /status).
 */
export const toPackageWriteBody = (pkg, range = usageLimitRange) => {
  const modes = pkg.supervision && Array.isArray(pkg.supervisionModes) ? pkg.supervisionModes : []
  const defaultEnabled =
    Boolean(pkg.supervision) &&
    (modes.includes(SUPERVISION_MODE.DEFAULT) || modes.includes(SUPERVISION_MODE.DYNAMIC))
  const dynamicEnabled = Boolean(pkg.supervision) && modes.includes(SUPERVISION_MODE.DYNAMIC)
  const carePlanEnabled = Boolean(pkg.carePlanAudit)

  const min = range?.min ?? DEFAULT_USAGE_RANGE.min
  const max = range?.max ?? DEFAULT_USAGE_RANGE.max
  let limit = Number(pkg.auditLimit)
  if (!Number.isFinite(limit) || limit < min) limit = min
  if (limit > max) limit = max

  let usageLimit
  if (!carePlanEnabled) {
    usageLimit = { enabled: false }
  } else if (pkg.auditUnlimited) {
    usageLimit = { enabled: true, unlimited: true }
  } else {
    usageLimit = { enabled: true, unlimited: false, limit }
  }

  return {
    name: String(pkg.name || '').trim(),
    supervision: { defaultEnabled, dynamicEnabled },
    carePlanEnabled,
    usageLimit,
  }
}

/** After a successful write, sync the unlimited UI flag for this server id. */
export const syncPackageUiFlagsFromPkg = pkg => {
  if (!pkg || isTempPackageId(pkg.id)) return
  if (pkg.carePlanAudit && pkg.auditUnlimited) {
    setPackageUiFlags(pkg.id, { auditUnlimited: true })
  } else {
    setPackageUiFlags(pkg.id, { auditUnlimited: false })
  }
}

// —— in-memory catalog cache (sync readers) ——————————————————————————————

let packagesCache = []

export const getPackagesCache = () => packagesCache.slice()

export const setPackagesCache = list => {
  packagesCache = Array.isArray(list) ? list.map(p => ({ ...p, id: String(p.id) })) : []
  return packagesCache.slice()
}

export const upsertPackageInCache = pkg => {
  if (!pkg) return getPackagesCache()
  const id = String(pkg.id)
  const next = packagesCache.filter(p => String(p.id) !== id)
  next.push({ ...pkg, id })
  packagesCache = next
  return getPackagesCache()
}

export const removePackageFromCache = id => {
  const key = String(id)
  packagesCache = packagesCache.filter(p => String(p.id) !== key)
  return getPackagesCache()
}

/**
 * Sync snapshot of the last fetched catalog. Prefer {@link fetchPackages} to
 * refresh; this exists so TierSelect / org pills / Manage Limits keep working
 * without every caller becoming async mid-render.
 */
export const loadPackages = () => getPackagesCache()

/**
 * @deprecated Catalog persistence is the packages API. Kept as a no-op cache
 * writer so any leftover callers do not throw.
 */
export const savePackages = list => {
  setPackagesCache(list)
  return true
}

/** Fetch from API, replace cache, return UI-shaped packages. */
export const fetchPackages = ({ status, signal } = {}) =>
  listAllPackages({ status, signal }).then(dtos => {
    const mapped = (dtos || []).map(fromPackageResponse)
    setPackagesCache(mapped)
    return mapped
  })

export const enabledCount = pkg =>
  Number(Boolean(pkg.supervision)) + Number(Boolean(pkg.carePlanAudit))

/** Basic / Gold / Platinum from the feature count, or null when nothing is on. */
export const tierLevelFor = pkg => {
  const n = enabledCount(pkg)
  return n <= 0 ? null : TIER_LEVELS[Math.min(n, TIER_LEVELS.length) - 1]
}

/**
 * The features a package includes, as display rows: an icon, a one-line title,
 * and a short detail. Used by the picker so each card shows exactly what it
 * packs — the way a streaming plan lists what you get.
 */
export const tierFeatureRows = pkg => {
  const rows = []

  if (pkg.supervision) {
    const modes = pkg.supervisionModes || []
    const detail = modes.length
      ? modes.map(mode => SUPERVISION_MODE_LABEL[mode]).join(' + ')
      : 'No session type set'
    rows.push({
      key: 'supervision',
      icon: ICON.users,
      title: 'Supervision',
      detail: `${detail} session${modes.length > 1 ? 's' : ''}`,
    })
  }

  if (pkg.carePlanAudit) {
    const detail = pkg.auditUnlimited
      ? 'Unlimited audits / month'
      : `${pkg.auditLimit || 0} audits / month`
    rows.push({
      key: 'carePlanAudit',
      icon: ICON.clipboardCheck,
      title: 'Care Plan Audit',
      detail,
    })
  }

  return rows
}
